import { Filters } from '@/core/collections/filters/filter.interface';
import { ApiResource } from '@/core/models/api-resource.interface';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import activity, { ActivityState } from './activities/activity.store';
import address, { AddressState } from './addresses/address.store';
import contact, { ContactState } from './contacts/contact.store';
import { Customer } from './customer.interface';
import { CUSTOMER_INCLUDES, CustomerService } from './customer.service';
import notes, { CustomerNoteState } from './notes/note.store';

const service = new CustomerService();

export interface CustomerState {
   customersOverview?: ApiResource<Array<Customer>>;
   filters?: Filters;
   query?: LocationQuery;
   activeIndex: number;
   updateActivities: boolean;

   //modules
   contact?: ContactState;
   address?: AddressState;
   activity?: ActivityState;
   notes?: CustomerNoteState;
}

const CustomerStore: Module<CustomerState, StoreState> = {
   namespaced: true,
   state: {
      filters: { quick_search: [], filter_elements: [] },
      activeIndex: 0,
      updateActivities: false,
   },
   getters: {
      customersOverview: (state) => state.customersOverview,
      filters: (state) => state.filters,
      query: (state) => state.query,
      activeIndex: (state) => state.activeIndex,
   },
   mutations: {
      SET_CUSTOMERS_OVERVIEW(state, customersOverview?: ApiResource<Array<Customer>>) {
         state.customersOverview = customersOverview;
      },
      SET_QUERY(state, query: LocationQuery) {
         state.query = query;
      },
      UPDATE_CUSTOMER_LIST(state, customer: Customer) {
         if (state.customersOverview) {
            const index = state.customersOverview?.data.findIndex((p) => p.id == customer.id);
            if (state.customersOverview?.data[index]) {
               const localCustomer = state.customersOverview?.data[index];
               const updatedCustomer = deepMergeObject(localCustomer, customer);

               state.customersOverview?.data.splice(index, 1, updatedCustomer);
               state.activeIndex = index;
            }
         }
      },
      SET_UPDATE_ACTIVITIES(state, updateActivities: boolean) {
         state.updateActivities = updateActivities;
      },
      DELETE_FROM_CUSTOMER_LIST(state, positionIds) {
         state.customersOverview!.data = state.customersOverview?.data.filter((c) => !positionIds.includes(c.id)) || [];
      },
   },
   actions: {
      async getFilteredCustomers(context, { query, filters }: { query: LocationQuery; filters: Filters }) {
         const customersOverview = await service.getFilteredCustomers(query, filters);
         return customersOverview;
      },
      async getCustomers(context, query: LocationQuery) {
         const response = await service.getCustomers({ ...query });
         return response;
      },
      async searchCustomers(context, query: LocationQuery) {
         const response = await service.getCustomers({
            per_page: '100',
            page: '1',
            include: CUSTOMER_INCLUDES.dropdown.includes.join(','),
            append: CUSTOMER_INCLUDES.dropdown.appends.join(','),
            ...query,
         });
         return response.data;
      },
      async autocomplete(context, search: string) {
         return await service.autocomplete(search);
      },
      async getCustomer(context, { customerId, query }: { customerId: number; query: LocationQuery }) {
         const response = await service.getCustomer(customerId, query);
         return response.data;
      },
      async createCustomer(context, customer: Customer) {
         const response = await service.postCustomer(customer);
         return response.data;
      },
      async putCustomer(context, customer: Customer) {
         const response = await service.putCustomer(customer);
         context.commit('UPDATE_CUSTOMER_LIST', { ...response, elastic_score: customer.elastic_score } as Customer);
         return response;
      },
      async patchCustomer(
         context,
         { customerId, patchCustomerData }: { customerId: number; patchCustomerData: { path: string; value: string | number } },
      ) {
         const response = await service.patchCustomer(customerId, patchCustomerData);
         return response.data;
      },
      async deletecustomersBulk(context, customerIds: Array<number>) {
         const response = await service.deleteCustomersBulk(customerIds);
         context.commit('DELETE_FROM_CUSTOMER_LIST', customerIds);
         return response;
      },
      async call(context, { customerId, hitId }: { customerId: number; hitId?: number }) {
         const response = await service.call(customerId, hitId);
         return response.data;
      },
      async inviteToVideoRoom(context, payload: { customerId: number; videocallRoomId: number }) {
         const { customerId, videocallRoomId } = payload;
         const response = await service.inviteToVideoRoom(customerId, videocallRoomId);
         return response.data;
      },
   },
   modules: {
      contact,
      address,
      activity,
      notes,
   },
};

export default CustomerStore;
